import { ChainID, MOON_POTS_CALE } from '@jdiyield/constants'
import { getBalanceNumber, getPriceTotalValueLock } from '@jdiyield/contracts'
import { Contract, PriceList, Stake, Token } from '@jdiyield/interfaces'
import BigNumber from 'bignumber.js'
import Web3 from 'web3'

import moonPotsABI from '../abi/moonPots.json'
import { calculate } from '../calculate'
import getNodeUrl from '../rpcRandom'
import { balanceOf, findToken, makeBatchRequest } from '../utils'

export const getMoonPots = async (
  walletAddress: string,
  contract: Contract,
  tokens: Token[],
  priceList: PriceList,
  web3Prop: Web3
): Promise<Stake[]> => {
  const { contractAddress, rewardAddress, routerAddress } = contract
  try {
    const web3 = web3Prop || new Web3(getNodeUrl(56))
    const ct = new web3.eth.Contract(moonPotsABI as any, contractAddress)

    const [balance, pendingReward, lpAddress] = await makeBatchRequest(web3, [
      ct.methods.userTotalBalance(walletAddress).call,
      ct.methods.earned(walletAddress).call,
      contractAddress === MOON_POTS_CALE ? ct.methods.underlying().call : ct.methods.stakeToken().call
    ])
    const reward = new BigNumber(pendingReward as string).dividedBy(`1e18`).toNumber()
    const stakeAmount = balance
    const rewardUSD = reward * priceList[rewardAddress]?.usd || 0
    const rewardSymbol = findToken(tokens, rewardAddress)?.symbol ?? '???'

    const balanceInfo = await calculate(
      web3,
      {
        lpAddress: lpAddress,
        pool: 0,
        availableBalanceOf: '0',
        balance: stakeAmount
      },
      routerAddress,
      tokens,
      priceList
    )
    const result: Stake = {
      ...contract,
      ...balanceInfo,
      reward: +reward,
      rewardSymbol,
      rewardUSD
    }

    return [result]
  } catch (e) {
    console.log('moonpots', e)
    return []
  }
}

export const getMoonpotAPR = async ({
  chainID,
  contractAddress,
  stakeTokenAddress,
  rewardAddress,
  tokens,
  priceList,
  lpUSD
}: {
  chainID: ChainID
  contractAddress: string
  stakeTokenAddress: string
  rewardAddress: string
  tokens: Token[]
  priceList: PriceList
  lpUSD?: number
}) => {
  const RPC_URL = getNodeUrl(chainID)
  const web3 = new Web3(RPC_URL)
  const ct = new web3.eth.Contract(moonPotsABI as any, contractAddress)
  const { periodFinish, rewardRate } = await ct.methods.rewardInfo(0).call()
  const rewardTokenDecimals = +`1e18`
  const rewardPrice =
    priceList?.[rewardAddress]?.usd || (await getPriceTotalValueLock(rewardAddress, chainID, tokens, priceList))
  const poolPrice = lpUSD || (await getPriceTotalValueLock(stakeTokenAddress, chainID, tokens, priceList))
  const weeklyRewards = Date.now() / 1000 > periodFinish ? 0 : (rewardRate / rewardTokenDecimals) * 604800
  const usdPerWeek = weeklyRewards * rewardPrice

  const stakeTvl = await balanceOf(contractAddress, stakeTokenAddress, chainID)
  const stakedTvlUSD = +getBalanceNumber(new BigNumber(stakeTvl)) * poolPrice
  const weeklyAPR = (usdPerWeek / stakedTvlUSD) * 100
  const dailyAPR = weeklyAPR / 7
  const monthlyAPR = dailyAPR * 30
  const yearlyAPR = weeklyAPR * 52
  const yearAPY = (Math.pow(1 + dailyAPR / 100, 365) - 1) * 100

  return {
    dailyAPR,
    weeklyAPR,
    monthlyAPR,
    yearlyAPR,
    yearAPY,
    rewardPrice
  }
}
